import type { ReactNode } from 'react';

export interface SuggestionChip {
  id: string;
  label: string;
}

export interface SuggestionChipsProps {
  /** Muted eyebrow above the row. */
  label?: string | null;
  /** Two in a fallback; as many as the context has in the narrow row. */
  chips: SuggestionChip[];
  onSelect: (id: string) => void;
  /** Fills that chip navy; anchors the thread in the narrow row. */
  selectedId?: string | null;
  /** `faq-row` is the below-1280 FAQ list: 44px chips, horizontal scroll with snap. */
  variant?: 'default' | 'faq-row';
  /** e.g. the "All N" drawer toggle in the narrow row. */
  trailing?: ReactNode;
}

export function SuggestionChips({
  label = null,
  chips,
  onSelect,
  selectedId = null,
  variant = 'default',
  trailing = null,
}: SuggestionChipsProps) {
  const isRow = variant === 'faq-row';

  return (
    <div className="flex flex-col gap-[9px]">
      {(label || trailing) && (
        <div className="flex items-center gap-2.5">
          {label && <span className="grow text-[10px] font-bold uppercase tracking-[0.09em] text-[#717182]">{label}</span>}
          {trailing}
        </div>
      )}

      <div className={isRow ? 'flex gap-2 overflow-x-auto [scroll-snap-type:x_mandatory]' : 'flex flex-wrap gap-2'}>
        {chips.map((chip) => {
          const selected = chip.id === selectedId;
          return (
            <button
              key={chip.id}
              type="button"
              onClick={() => onSelect(chip.id)}
              aria-current={selected ? 'true' : undefined}
              className={[
                'box-border rounded-full border font-semibold focus-visible:ring-[3px] focus-visible:ring-dvaOrange/35 focus-visible:outline-none',
                isRow ? 'flex h-11 shrink-0 items-center whitespace-nowrap px-4 text-[13px] [scroll-snap-align:start]' : 'px-3.5 py-[9px] text-[12.5px]',
                selected ? 'border-dvaNavy bg-dvaNavy text-white' : 'border-[#e5e7eb] bg-white text-dvaNavy hover:border-dvaNavy',
              ].join(' ')}
            >
              {chip.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
